import Image from "next/image";
import React, { type ReactElement } from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { BREAKPOINTS, type Context, formatPrice, getProductURL } from "@/globals";
import { getSearchAbo } from "@/globals.raabits";
import { getAncillaryAsset } from "@/raabits/constants";
import { PRODUCT_VARIANTS, type RSearchProductType } from "@/schemes/raabits.scheme";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";
import { LinkView } from "@/views/link.view";

import { ProductFachView } from "../product-page/product-fach.view";
import { ProductKlassenstufenView } from "../product-page/product-klassenstufen.view";
import { SearchItemBadgeView } from "./search-item-badge.view";

export enum ORIENTATION {
    landscape = "landscape",
    portrait = "portrait",
}

export type SearchItemProps = {
    context: Context;
    orientation: ORIENTATION;
    product: RSearchProductType;
};

export const SearchItemView = ({
    orientation,
    product,
    context,
}: SearchItemProps): ReactElement => {
    const abo = getSearchAbo(product, context);

    const metadata = product.isInRaabits
        ? {
              schulart: product.filterMetadata.schularten,
              fach: product.filterMetadata.fach,
              klassenstufen: product.filterMetadata.klassenstufen,
              lernjahre: product.filterMetadata.lernjahre,
          }
        : {
              klassenstufen: product.productVariant?.extendedAttributes.raaKlassenstufen
                  .split(",")
                  .filter(Boolean),
              schulart:
                  (product.productVariant?.extendedAttributes.schoolType && [
                      product.productVariant?.extendedAttributes.schoolType,
                  ]) ||
                  undefined,
              fach: product.productVariant?.extendedAttributes.subjectArea,
              price: product.productVariant?.price.price,
          };

    const imageUrl =
        product.key && product.coverimage
            ? getAncillaryAsset(product.key, product.coverimage)
            : undefined;

    const fach = metadata.fach && (context.faecher[metadata.fach] || metadata.fach);

    if (orientation === ORIENTATION.portrait) {
        return (
            <div className="w-full relative">
                <LinkView
                    className={twJoin(
                        "flex flex-col justify-start items-center rounded-lg overflow-hidden bg-white h-full",
                    )}
                    prefetch={false}
                    href={getProductURL(product.title, product.rnummer)}
                >
                    {metadata.schulart && (
                        <div className="bg-brandDark px-4 py-2 w-full">
                            <p
                                className={twMerge(
                                    TYPO_STYLES.bodyExtraBold16(),
                                    "text-white truncate",
                                )}
                            >
                                {[...metadata.schulart]
                                    .sort((a, b) => a.localeCompare(b))
                                    .join(" | ")}
                            </p>
                        </div>
                    )}
                    {imageUrl ? (
                        <div className="aspect-ratio-box">
                            <SearchItemBadgeView product={product} />
                            <Image
                                src={imageUrl}
                                alt=""
                                width={160}
                                height={90}
                                className="aspect-ratio-img"
                                sizes={`(min-width: ${BREAKPOINTS.sm}px) ${BREAKPOINTS.sm}px, 100vw`}
                            />
                        </div>
                    ) : (
                        <div className="aspect-ratio-box bg-grey3"></div>
                    )}
                    <div
                        className={twMerge(
                            WRAPPER_STYLES.VLeftBetween(),
                            "pt-4 pb-6 px-6 w-full gap-4 h-full",
                        )}
                    >
                        <h2 className="text-brand-dark text-2xl font-extrabold leading-8 h-[96px] overflow-hidden">
                            {product.title +
                                (product.productVariant?.extendedAttributes.productVariant ===
                                PRODUCT_VARIANTS.BRO_EBK
                                    ? " (eBook)"
                                    : "")}
                        </h2>
                        <div className="flex flex-col gap-3">
                            {fach && <ProductFachView fach={fach} />}
                            {metadata.klassenstufen && metadata.klassenstufen.length > 0 && (
                                <ProductKlassenstufenView
                                    lernjahre={metadata.lernjahre}
                                    klassenstufen={metadata.klassenstufen}
                                />
                            )}
                        </div>
                        <div
                            className={twMerge(
                                WRAPPER_STYLES.HBottomBetween(),
                                "gap-2 pt-5 w-full",
                            )}
                        >
                            <div className={WRAPPER_STYLES.VCenterLeft()}>
                                {!product.isInRaabits && (
                                    <p className={TYPO_STYLES.headlinePlain5()}>
                                        {formatPrice(metadata.price)}
                                    </p>
                                )}
                                <p className={twMerge(TYPO_STYLES.bodyNormal14(), "text-grey7")}>
                                    {abo}
                                </p>
                            </div>
                            <div className={BUTTON_STYLES.secondary()}>
                                <span className={twMerge(TYPO_STYLES.icon(), "text-4xl")}>
                                    {String.fromCharCode(Number("0xe938"))}
                                </span>
                            </div>
                        </div>
                    </div>
                </LinkView>
            </div>
        );
    } else {
        return (
            <div className="w-full relative">
                <LinkView
                    className="flex flex-col justify-start items-center rounded-lg overflow-hidden bg-white h-full"
                    href={getProductURL(product.title, product.rnummer)}
                    prefetch={false}
                >
                    {metadata.schulart && (
                        <div className="bg-brandDark px-4 py-2 w-full">
                            <p
                                className={twMerge(
                                    TYPO_STYLES.bodyExtraBold16(),
                                    "text-white truncate",
                                )}
                            >
                                {[...metadata.schulart]
                                    .sort((a, b) => a.localeCompare(b))
                                    .join(" | ")}
                            </p>
                        </div>
                    )}
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2">
                        {imageUrl ? (
                            <div className="aspect-ratio-box">
                                <SearchItemBadgeView product={product} />
                                <Image
                                    src={imageUrl}
                                    alt=""
                                    width={160}
                                    height={90}
                                    className="aspect-ratio-img"
                                    sizes={`(min-width: ${BREAKPOINTS.sm}px) ${BREAKPOINTS.sm}px, 100vw`}
                                />
                            </div>
                        ) : (
                            <div className="aspect-ratio-box bg-grey3"></div>
                        )}

                        <div
                            className={twMerge(
                                WRAPPER_STYLES.VLeftBetween(),
                                "pt-4 pb-6 px-6 w-full h-full",
                            )}
                        >
                            <h2 className="text-brand-dark text-2xl font-extrabold leading-8 overflow-hidden min-h-24">
                                {product.title}
                            </h2>
                            <div className={twMerge(WRAPPER_STYLES.VCenterLeft(), "w-full")}>
                                <div className="flex flex-col gap-3">
                                    {fach && <ProductFachView fach={fach} />}
                                    {metadata.klassenstufen &&
                                        metadata.klassenstufen.length > 0 && (
                                            <ProductKlassenstufenView
                                                lernjahre={metadata.lernjahre}
                                                klassenstufen={metadata.klassenstufen}
                                            />
                                        )}
                                </div>
                                <div
                                    className={twMerge(
                                        WRAPPER_STYLES.HBottomBetween(),
                                        "gap-2 w-full",
                                    )}
                                >
                                    <p
                                        className={twMerge(
                                            TYPO_STYLES.bodyNormal14(),
                                            "text-grey7",
                                        )}
                                    >
                                        {abo}
                                    </p>
                                    <div className={BUTTON_STYLES.secondary()}>
                                        <span className={twMerge(TYPO_STYLES.icon(), "text-4xl")}>
                                            {String.fromCharCode(Number("0xe938"))}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </LinkView>
            </div>
        );
    }
};
